import { formatValue, setCaret } from '../../common/format';
import util from '../../common/util';
import Autocomplete from '../../components/map-here/js/autocomplete';
import { mapState } from 'pinia';
import { usePageStore } from '@/store';

function getSuggestion(autocomplete) {
    const suggest = [];
    try {
        if (autocomplete.getSuggestion) {
            return autocomplete.getSuggestion();
        }
        let sgst = autocomplete.gm_accessors_.place.Yc.predictions;
        for (let i = 0, len = sgst.length; i < len; i++) {
            suggest.push(sgst[i].li);
        }
    } catch (e) {
        console.error(e);
    }
    return suggest;
}

export default {
    emits: ['update:searchText', 'focus', 'blur', 'submitAddress'],
    i18next: {
        ns: 'common'
    },
    props: {
        placeholder: {
            type: String,
            default: undefined
        },
        inputBoderColor: {
            type: String,
            default: ''
        },
        submitText: {
            type: String,
            default: 'Submit'
        },
        submitStyle: {
            type: Object,
            default: () => ({})
        },
        country: {
            type: String,
            default: ''
        },
        showUnitField: {
            type: Boolean,
            default: false
        },
        autoFocus: {
            type: Boolean,
            default: true
        },
        chooseSuggestionAutoSubmit: {
            type: Boolean,
            default: false
        },
        autoMatchSuggestion: {
            type: Boolean,
            default: false
        },
        mobileScroll: {
            type: Boolean,
            default: true
        },
        icon: {
            type: String,
            default: ''
        },
        searchText: {
            type: String,
            default: ''
        },
        submitBtnStyle: {
            type: String,
            default: ''
        },
        theme: {
            type: String,
            default: ''
        }
    },
    data: function() {
        return {
            address: '', // input v-model
            unit: '',
            errorShow: false,
            submitDisabled: false
        };
    },
    watch: {
        address() {
            this.$emit('update:searchText', this.address);
        },
        searchText: {
            immediate: true,
            handler: function() {
                this.address = this.searchText;
            }
        }
    },
    computed: {
        ...mapState(usePageStore, {
            page: store => store.page,
            currencySymbol: store => store.currency.symbol
        }),
        isMobile() {
            return this.platform.isMobile
        },
        compCountry({ page }) {
            return this.country || page.country || 'usa';
        },
        cmpPlaceholder() {
            return this.placeholder ?? this.$t('common:placeholder.address');
        }
    },
    mounted() {
        // auto focus
        if (this.autoFocus && !this.isMobile && util.isOnView(this.$el)) {
            util.timeout({
                handler: () => {
                    this.$refs.input?.focus();
                },
                delay: 350,
                unique: true
            });
        }
    },
    methods: {
        addressFormat(value, doInput = false) {
            let result = formatValue(this.$refs.input, {
                oldValue: this.address,
                value: value,
                mask: 'search',
                currencySymbol: this.currencySymbol
            });
            if (!result) {
                return value;
            }
            if (doInput) {
                this.$nextTick(() => setCaret(this.$refs.input, result.caret));
            }
            return result.value;
        },
        //  initialization  autocomplete( about to input -> autocomplete)
        initAutocomplete() {
            if (!this.autocomplete) {
                // Autocomplete()  exist  input Carried on autoComplete package of (suggestions The business logic is also encapsulated inside )
                // this.autocomplete = {getPlace,getSuggestion}
                this.autocomplete = Autocomplete(
                    this.$refs.input,
                    this.compCountry,
                    //  chosen suggestion callback after 
                    () => {
                        this.address = this.$refs.input?.value;
                        if (this.chooseSuggestionAutoSubmit || this.isMobile) {
                            //  Auto trigger on mobile 
                            this.onSubmit();
                        }
                    }
                );
            }
        },
        // input focus
        onFocus() {
            !this.autocomplete && this.initAutocomplete();
            if (this.isMobile && this.mobileScroll) {
                this.$refs.input?.scrollIntoView({
                    block: 'center',
                    inline: 'nearest'
                });
                let num = 0;
                util.timeout({
                    handler: () => {
                        if (num === 5) {
                            return false; 
                        }
                        this.$refs.input?.scrollIntoView({
                            block: 'center',
                            inline: 'nearest'
                        });
                        num += 1;
                    },
                    once: false,
                    delay: 50
                });
            }
            this.$emit('focus');
        },
        onUnitFocus() {
            this.$emit('focus');
        },
        onInput($event) {
            if (!this.lockedInput) {
                this.address = this.addressFormat($event.target.value, true);
            }
        },
        onCompositionstart() {
            this.lockedInput = true;
        },
        onCompositionend($event) {
            this.lockedInput = false;
            this.address = this.addressFormat($event.target.value, true);
        },
        onBlur() {
            this.$emit('blur');
        },
        onKeyDown($event) {
            const code = $event.keyCode;
            if (code === 13) {
                $event.preventDefault();
                this.onSubmit();
                this.autocomplete.close();
            }
        },
        //  monitor submit event 
        onSubmit() {
            const address = this.$refs.input.value.trim();
            if (address) {
                this.submitDisabled = true;
                setTimeout(() => {
                    this.submitDisabled = false;
                }, 3000);
                this.submitAddress();
            } else {
                this.showError();
            }
        },
        submitAddress() {
            let address = this.$refs.input.value;
            const data = {
                inp: address,
                suggestion: '',
                unit: this.unit
            };
            try {
                if (this.autocomplete) {
                    const autocomplete = this.autocomplete;
                    const place = autocomplete.getPlace();

                    if (place && place.address_components) {
                        data.suggestion = getSuggestion(autocomplete).join(';');
                        data.type = 'choose-suggestion';
                    } else {
                        let suggestion = getSuggestion(autocomplete);
                        if (
                            document.body.querySelector(
                                '.pac-container .pac-item'
                            )
                        ) {
                            if (
                                this.autoMatchSuggestion &&
                                suggestion.length &&
                                address !== suggestion[0]
                            ) {
                                address = suggestion[0];
                                data.type = 'match-suggestion';
                            }
                            data.suggestion = suggestion.join(';');
                        } else {
                            let sgst = suggestion.join(';');
                            if (sgst.substr(0, address.length) === address) {
                                data.suggestion = sgst;
                            }
                        }
                        if (!data.type) {
                            data.type = !data.suggestion
                                ? 'miss-suggestion'
                                : suggestion.indexOf(address) !== -1
                                    ? 'choose-suggestion'
                                    : 'ignore-suggestion';
                        }
                    }
                } else {
                    data.type = 'miss-suggestion';
                }
            } catch (error) {
                console.log(error);
            }
            this.address = address;
            this.$refs.input.blur();
            this.$emit('submitAddress', address, data, () => {
                this.clearAddress();
            });
        },
        showError() {
            this.errorShow = true;
            util.timeout({
                handler: () => {
                    this.errorShow = false;
                },
                delay: 3000,
                unique: true
            });
        },
        clearAddress(focus = true) {
            this.address = '';
            if (this.$refs.input) {
                this.$refs.input.value = '';
                focus && this.$refs.input.focus();
            }
            this.autocomplete && this.autocomplete.close();
        }
    }
};
